import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DeleteIcon from '@material-ui/icons/Delete';
import { Controller, useFormContext } from 'react-hook-form';
import { isEmpty, isFunction, isNil, map, merge, without } from 'lodash';
import { t } from '@lingui/macro';
import DataTable from '../data-table/DataTable';
import { useSelectorStyles } from './ListSelectorStyles';
import { SELECTIONMODE_MULTI, SELECTIONMODE_NONE } from '../data-table/SelectionContext';
import ListSelectorDialog from './ListSelectorDialog';
import { COLUMN_TYPES } from '../data-table/DataTableColumns';
import NgpFormButton from './NgpFormButton';
import { filterEntities } from './ListSelectorUtils';
import { useArrayLoader, useQueryPostLoader } from '../data-table/Loaders';

export default function AgentSelector({
    gridColumns,
    dbSelected = [],
    fieldName,
    addButtonText,
    removeButtonText,
    setSelectionIsDirty,
    isMultiRemove = true,
    selectedEmptyGridText,
    updateSelectionFunction,
    resolveInvertedFunction,
    interfaceType,
    agentList,
    sorters,
    selectedAgents,
    setTheAgents,
    dataArray = null,
    dataArraySearchFields = [],
    selectedDataSource,
    selectedLoaderFunction,
}) {
    const classes = useSelectorStyles();
    const { setValue, control, getValues, watch } = useFormContext();
    const selected = useRef(dbSelected);
    // Keep `selected` in sync with the form field value so that external resets
    // (e.g. switching the nPoint interface type from wired to wireless) are
    // reflected when the ListSelectorDialog filters out already-selected items.
    const watchedFieldValue = fieldName ? watch(fieldName) : undefined;
    useEffect(() => {
        if (Array.isArray(watchedFieldValue)) {
            selected.current = watchedFieldValue;
        }
    }, [watchedFieldValue]);

    const [theSelectedGridColumns, setTheSelectedGridColumns] = useState(gridColumns);
    const [removeDisabled, setRemoveDisabled] = useState(false);
    const [tableSelection, setTableSelection] = useState(undefined);
    const [showDialog, setShowDialog] = useState(false);

    const staticSelectedData = useMemo(() => selectedAgents ?? [], [selectedAgents]);
    const staticDataFilterCallback = useCallback(
        (entities, filters) => filterEntities(entities, filters, dataArraySearchFields),
        [dataArraySearchFields]
    );
    const selectedLoaderFn = !isNil(selectedAgents) ? useArrayLoader : selectedLoaderFunction || useQueryPostLoader;
    const selectedLoader = !isNil(selectedAgents)
        ? selectedLoaderFn(staticSelectedData, staticDataFilterCallback)
        : selectedLoaderFn(selectedDataSource, selected.current, false);

    useEffect(() => {
        const columns = gridColumns;
        setTheSelectedGridColumns(
            map([...deleteColumnDef, ...columns], (theColumn) => merge(theColumn, { resizable: false }))
        );
        // Only want to run once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTableSelectionChange = (currentTableSelection) => {
        setTableSelection(currentTableSelection);

        setRemoveDisabled(
            !currentTableSelection || (isEmpty(currentTableSelection.selection) && !currentTableSelection.inverted)
        );
    };

    const selectionOptions = isMultiRemove
        ? {
              mode: SELECTIONMODE_MULTI,
              allowSelectAll: true,
              idMapping: '_id',
              onChange: handleTableSelectionChange,
          }
        : { mode: SELECTIONMODE_NONE };

    const updateSelection = (selectedIds) => {
        selected.current = selectedIds;

        if (isFunction(updateSelectionFunction)) {
            updateSelectionFunction(selectedIds);
        } else {
            setValue(fieldName, selectedIds);
        }

        if (isFunction(setSelectionIsDirty)) {
            setSelectionIsDirty(true);
        }
    };

    const removeSelection = () => {
        const { agents } = getValues();
        const newSelection = {
            selection: agents.filter((selectedAgent) =>
                tableSelection?.inverted
                    ? tableSelection.selection?.includes(selectedAgent)
                    : !tableSelection.selection?.includes(selectedAgent)
            ),
        };
        setTheAgents(newSelection);
        setValue('agents', newSelection.selection);
        updateSelection(newSelection.selection);
    };

    const removeOne = (record) => {
        const { agents } = getValues();
        setTheAgents(without(agents, record._id));
        setValue('agents', without(agents, record._id));
        updateSelection(without(agents, record._id));
    };

    const deleteColumnDef = [
        {
            type: COLUMN_TYPES.ICON,
            title: '',
            width: 45,
            icons: [
                {
                    tooltip: t`Delete`,
                    getIcon: () => {
                        // just show the icon
                        return <DeleteIcon />;
                    },
                    clickHandler: removeOne,
                },
            ],
        },
    ];

    return (
        <>
            <Controller
                control={control}
                name={fieldName}
                render={() => (
                    <>
                        <div className={classes.selectorContainer}>
                            <div className={classes.selectorActions}>
                                <NgpFormButton onClick={() => setShowDialog(true)}>{addButtonText}</NgpFormButton>
                                {isMultiRemove && (
                                    <NgpFormButton onClick={removeSelection} disabled={removeDisabled}>
                                        {removeButtonText}
                                    </NgpFormButton>
                                )}
                            </div>
                            <DataTable
                                loader={selectedLoader}
                                columns={theSelectedGridColumns}
                                showSelectionColumn={isMultiRemove}
                                disableCSVExport
                                disableColumnRemover
                                sorters={sorters}
                                selectionOptions={selectionOptions}
                                emptyText={selectedEmptyGridText}
                                interfaceType={interfaceType}
                                updateSelection={updateSelection}
                            />
                        </div>

                        <ListSelectorDialog
                            gridColumns={gridColumns}
                            dbSelected={dbSelected}
                            availableSelectAll
                            addButtonText={addButtonText}
                            showDialog={showDialog}
                            setShowDialog={setShowDialog}
                            selected={Array.isArray(watchedFieldValue) ? watchedFieldValue : selected.current}
                            updateSelection={updateSelection}
                            resolveInvertedFunction={resolveInvertedFunction}
                            interfaceType={interfaceType}
                            agentList={agentList}
                            setTheAgents={setTheAgents}
                            sorters={sorters}
                            dataArray={dataArray}
                            dataArraySearchFields={dataArraySearchFields}
                        />
                    </>
                )}
            />
        </>
    );
}
